import * as AuthSession from "expo-auth-session";
import { authSuccess, logoutUser, useAuthState } from "./State";
import { useCallback, useEffect, useState } from "react";
import { config } from "../../config";
import { registerLogin } from "../../common/analytics";
import { addBreadcrumb } from "../../common/sentry";
import { Linking, Platform } from "react-native";
import { init } from "@paralleldrive/cuid2";
import * as WebBrowser from "expo-web-browser";
import * as Sentry from "@sentry/react-native";
import { useAlert } from "@/src/common/Alert";
if (Platform.OS === "web") {
  WebBrowser.maybeCompleteAuthSession();
}

const createId = init();

export const useLogin = (
  signup = false,
  org?: {
    organization: string;
    invitation: string;
  },
  emailHint?: string
) => {
  const [isLoginIn, setIsLoggingIn] = useState(false);
  const [, dispatch] = useAuthState();

  const redirectUri = AuthSession.makeRedirectUri({
    native: config.production
      ? "storeapp://redirect"
      : "storebetaapp://redirect",
    preferLocalhost: false,
  });

  const [state, setState] = useState(() => createId());

  const [hasErrored, setHasErrored] = useState(false);

  const extraParams = {
    screen_hint: signup ? "signup" : "login",
    ...(emailHint ? { email_hint: emailHint } : {}),
    ...(org
      ? { organization: org.organization, invitation: org.invitation }
      : {}),
  };

  const [req, , prompt] = AuthSession.useAuthRequest(
    {
      clientId: config.auth.oauth.clientId,
      redirectUri,
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
      state,
      scopes: ["openid", "profile", "email", "offline_access"],
      prompt: AuthSession.Prompt.Login,
      extraParams: hasErrored ? undefined : extraParams,
    },
    config.auth.oauth.serviceConfiguration
  );
  const Alert = useAlert();
  const handleError = (errorMessage: string) => {
    setHasErrored(true);
    Alert.error("Login failed", errorMessage);
  };

  const handleResult = async (
    authResult: null | AuthSession.AuthSessionResult
  ) => {
    if (
      authResult?.type === "success" &&
      authResult.params.code &&
      authResult.params.state === req?.state
    ) {
      const details = {
        grant_type: "authorization_code",
        client_id: config.auth.oauth.clientId,
        code_verifier: req?.codeVerifier,
        code: authResult.params.code,
        redirect_uri: req?.redirectUri,
      } as any;

      const formBody = Object.keys(details)
        .map(
          (key) =>
            encodeURIComponent(key) + "=" + encodeURIComponent(details[key])
        )
        .join("&");

      try {
        const res = await (
          await fetch(`${config.auth.domain}/oauth/token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: formBody,
          })
        ).json();

        const expiresAt = res.expires_in
          ? Date.now() + res.expires_in * 1000
          : null;

        setIsLoggingIn(false);
        dispatch(
          authSuccess(
            res.access_token,
            res.id_token,
            res.refresh_token,
            expiresAt,
            false
          )
        );
        registerLogin();
        addBreadcrumb({
          type: "ui",
          category: "ui.login.success",
        });
      } catch (e: any) {
        addBreadcrumb({
          type: "ui",
          category: "ui.login.failiure",
          data: e,
        });

        Sentry.captureException(e);
        setIsLoggingIn(false);
      }
    } else {
      if (authResult?.type === "error") {
        handleError(authResult.params?.error_description ?? "Unknown error");
        Sentry.captureMessage("Login failed", {
          extra: {
            errorCode: authResult?.error?.code,
            errorMessage: authResult?.error?.message,
            params: authResult?.params,
          },
        });
      } else {
        Sentry.captureMessage("Login not completed", {
          extra: {
            authResult: authResult?.type,
          },
        });
      }
      setIsLoggingIn(false);
    }
  };

  let cb = (onCompleted?: () => void) => {
    if (isLoginIn) {
      return;
    }
    setIsLoggingIn(true);
    addBreadcrumb({
      type: "ui",
      category: "ui.login.init",
    });

    prompt({
      windowFeatures: {
        width: 800,
        height: 800,
      },
    })
      .then((e) => {
        return handleResult(e).then((r) => {
          onCompleted?.();
          return r;
        });
      })
      .catch((e) => {
        Sentry.captureException(e);
        handleError("Unknown error");
      })
      .finally(() => {
        setState(createId());
        setIsLoggingIn(false);
      });
  };

  const [testLoginCompleted, setTestLoginCompleted] = useState(false);
  if (testLoginCompleted) {
    cb = (onCompleted?: () => void) => onCompleted?.();
  }
  useEffect(() => {
    if (process.env.EXPO_PUBLIC_TESTING === "true") {
      const listener = Linking.addEventListener("url", ({ url }) => {
        if (url.startsWith("storebetaapp://set-login?")) {
          const params = new URLSearchParams(
            url.substring("storebetaapp://set-login?".length)
          );

          const expiresIn = parseFloat(params.get("expires_in")!);

          const expiresAt = expiresIn ? Date.now() + expiresIn * 1000 : null;

          dispatch(
            authSuccess(
              params.get("access_token")!,
              params.get("id_token")!,
              params.get("refresh_token")!,
              expiresAt,
              false
            )
          );
          registerLogin();
          addBreadcrumb({
            type: "ui",
            category: "ui.login.success",
          });
          setTestLoginCompleted(true);
        }
      });
      return () => listener.remove();
    }
  }, [dispatch]);

  const isReady = !!req;

  return [cb, isLoginIn, isReady, hasErrored] as const;
};

export const useLogout = () => {
  const [state, dispatch] = useAuthState();

  const cb = useCallback(() => {
    dispatch(logoutUser(state.currentAuthSessionIndex ?? 0));
  }, [state.currentAuthSessionIndex, dispatch]);
  return cb;
};
